import { Alert, Button, Divider, Form, FormInstance, message, Modal, Result, Space, Steps, Table, Tag, Upload, UploadFile, UploadProps } from "antd"
import { forwardRef, useEffect, useRef, useState, useImperativeHandle } from "react";
import Server from "../../server/Server";
import URL from "../../server/URL";
import EUtils from "../../utils/EUtils";

interface IEImportFile {
    maxCount?: number,
    importExcelType: string,
    title?: string,
    businessInfo?: string,
    reLoad?: () => void
}

const EImportFile = forwardRef((props: IEImportFile, ref) => {
    const { maxCount, title, importExcelType, businessInfo, reLoad } = props;
    const [visible, setVisible] = useState(false);
    const formRef = useRef<FormInstance>(null);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [fileInfo, setFileInfo] = useState<any>(null);
    const [showError, setShowError] = useState(false);
    const [dataSource, setDataSource] = useState<any>([]);

    useImperativeHandle(ref, () => ({
        setVisible
    }))

    const onChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
        setFileList(newFileList);
    };
    
    const beforeUpload = (file: any) => {
        if (file) {
            const formData = new FormData();
            formData.append('file', file);
            // 发送请求
            Server.UpLoad(URL.Common.UpLoadFile, formData)
                .then((response: any) => {
                    if (response.data && response.data.isSuccess) {
                        setFileInfo(response.data.fileInfo)
                    } else {
                        message.error('文件上传失败');
                    }
                })
        }

        return false; // Prevent default upload behavior
    };

    const config = {
        maxCount: maxCount ?? 1,
        fileList: fileList,
        onChange: onChange,
        beforeUpload: beforeUpload,
    }

    const steps = [
        {
            title: '文件上传',
            content: <div>
                <Form
                    ref={formRef}
                    labelCol={{ span: 4 }}
                    wrapperCol={{ span: 20 }}>
                    <Form.Item label={'文件上传'}
                        name={'file'}
                        rules={[{ required: true, message: '请选择需要上传的文件' }]}>
                        <Upload {...config}>
                            {fileList && fileList?.length < (maxCount ?? 1) ? <Button style={{ width: '100px' }}>上传</Button> : ''}
                        </Upload>
                    </Form.Item>
                </Form>
                <div style={{ display: (showError ? 'block' : 'none') }}>
                    <Divider style={{ margin: '5px 0' }} />
                    <Alert message="导入失败：格式校验失败，失败详情已用批注形式标注在Excel文件中，请下载文件修订后，重新上传！" banner />
                    <Table
                        size="small"
                        bordered
                        columns={[
                            { title: '导入内容', dataIndex: 'content', key: 'content' },
                            {
                                title: '校验通过', align: 'right', dataIndex: 'successCount', key: 'successCount',
                                render(value, record, index) {
                                    return <span style={{ color: 'green' }}>{value}</span>
                                },
                            },
                            {
                                title: '校验失败', align: 'right', dataIndex: 'errorCount', key: 'errorCount',
                                render(value, record, index) {
                                    return <span style={{ color: 'red' }} > {value}</span>
                                },
                            },
                            {
                                title: '操作', align: 'center', width: 100,
                                render(value, record, index) {
                                    return <Button type="link" size="small" onClick={() => {
                                        var errorFileInfo = JSON.parse(record.errorFileInfo)
                                        var request = {
                                            fileInfo: record.errorFileInfo
                                        };
                                        Server.DownLoad(URL.Common.DownLoadFile, request)
                                            .then((response: any) => {
                                                var fileName = (title ?? '文件导入') + '错误' + errorFileInfo.ExtentionName;
                                                EUtils.DownLoadFile(response.data, fileName);
                                            })
                                    }} >下载文件</Button>
                                },
                            }
                        ]}
                        dataSource={dataSource}
                        pagination={false} />
                </div>
            </div >,
        },
        {
            title: '导入完成',
            content: <Result status={'success'} title={'导入成功'} subTitle={'点击完成按钮关闭窗口后，列表数据自动刷新。'}></Result>,
        },
    ];

    const [current, setCurrent] = useState(0);

    const items = steps.map((item: any) => ({ key: item.title, title: item.title, status: item.status }));

    useEffect(() => {
        if (visible) {
            formRef.current?.resetFields();
            setShowError(false);
            setFileList([]);
            setFileInfo(null);
            setCurrent(0);
        }
    }, [visible])

    const onNext = () => {
        formRef.current?.validateFields().then((values => {
            setShowError(false);
            var request = {
                importExcelType: importExcelType ?? '',
                fileInfo: JSON.stringify(fileInfo),
                businessInfo: (businessInfo ?? '/')
            }
            Server.Post(URL.Common.ImportExcel, request)
                .then((response: any) => {
                    if (response.isSuccess) {
                        if (response.errorCount > 0) {
                            var totalCount = response.successCount + response.errorCount;
                            var data: any = [{
                                content: (title ?? '文件导入') + "(" + totalCount + "条)",
                                totalCount: totalCount,
                                successCount: response.successCount,
                                errorCount: response.errorCount,
                                errorFileInfo: response.errorFileInfo
                            }];
                            setDataSource(data)
                            setFileList([])
                            setFileInfo(null);
                            formRef.current?.resetFields();
                            setShowError(true);
                        } else {
                            setDataSource([])
                            setShowError(false);
                            setCurrent(current + 1);
                        }
                    } else {
                        message.warning(response.returnMsg);
                    }
                })
        })).catch((info: any) => {
            console.log(info);
        });
    }

    const onCancel = () => {
        setVisible(false);
    }

    return <Modal
        title={title ?? '文件导入'}
        width={800}
        maskClosable={false}
        open={visible}
        onCancel={() => { onCancel() }}
        footer={<Space>
                {current < steps.length - 1 && (
                    <>
                        <Button type="primary" onClick={() => {
                            onNext();
                        }}>下一步</Button>
                        <Button onClick={onCancel}>取消</Button>
                    </>
                )}
                {current == steps.length - 1 && (
                    <Button type="primary" onClick={() => {
                        setVisible(false);
                        if (reLoad)
                            reLoad();
                    }}>完成</Button>)
                }
            </Space>
        }>
        <Steps
            size="small"
            type="navigation"
            current={current}
            className="site-navigation-steps"
            items={items}
        />
        <div style={{ minHeight: '300px', margin: '5px' }}>{steps[current].content}</div>
    </Modal>
})
export default EImportFile;